import React, { useEffect, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  ChartOptions,
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

import { STATUS_CODE, openNotificationWithIcon } from "../../../utils";
import { NotAccessibleFallback } from "../../../components";
import { Moment } from "moment";
import moment from "moment";
import { Select, Spin } from "antd";
const { Option } = Select;

interface TrendChartProps {
  fromDate: Moment;
  toDate: Moment;
  data: any[];
  title: string;
  description: string;
  dataKey: string;
  labelText: string;
  range?: string;
  defaultGranularity?: string;
  includeYears?: boolean;
}

export const TrendChart = ({
  fromDate,
  toDate,
  data,
  title,
  description,
  dataKey,
  labelText,
  range = "day",
  defaultGranularity = "days",
  includeYears = true,
}: TrendChartProps) => {
  const [loading, setLoading] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(true);
  const [timeGranularity, setTimeGranularity] = useState(defaultGranularity);
  const [availableGranularities, setAvailableGranularities] = useState<
    string[]
  >([]);
  const [chartData, setChartData] = useState<
    { month: string; value: number }[]
  >([]);

  useEffect(() => {
    const optionsByRange: Record<string, string[]> = includeYears
      ? {
          day: ["days", "weeks", "months", "years"],
          week: ["weeks", "months", "years"],
          month: ["months", "years"],
          year: ["years"],
        }
      : {
          day: ["days", "weeks", "months"],
          week: ["weeks", "months"],
          month: ["months"],
        };

    setAvailableGranularities(optionsByRange[range] || ["days"]);

    const validGranularities = optionsByRange[range] || ["days"];
    if (!validGranularities.includes(timeGranularity)) {
      setTimeGranularity(validGranularities[0]);
    }
  }, [range, timeGranularity]);

  const processData = (granularity: string) => {
    const result: { month: string; value: number }[] = [];
    const dataMap = new Map<string, number>();

    data.forEach((item) => {
      const dateValue = item.date;
      const dataValue = item[dataKey] || 0;

      let formattedDate = "";

      if (granularity === "weeks") {
        const isoWeek = moment(dateValue).isoWeek();
        const year = moment(dateValue).isoWeekYear();
        const startOfWeek = moment()
          .isoWeekYear(year)
          .isoWeek(isoWeek)
          .startOf("isoWeek");

        const weekOfMonth = Math.ceil(startOfWeek.date() / 7);
        formattedDate = `${startOfWeek.format(
          "MMM"
        )}-W${weekOfMonth}, '${startOfWeek.format("YY")}`;
      } else if (granularity === "months") {
        formattedDate = moment(dateValue).format("MMM YYYY");
      } else if (granularity === "days") {
        formattedDate = moment(dateValue).format("DD MMM");
      } else {
        formattedDate = moment(dateValue).format("YYYY");
      }

      if (dataMap.has(formattedDate)) {
        dataMap.set(formattedDate, dataMap.get(formattedDate)! + dataValue);
      } else {
        dataMap.set(formattedDate, dataValue);
      }
    });

    dataMap.forEach((value, month) => {
      result.push({ month, value });
    });

    return result;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const processedData = processData(timeGranularity);
        setChartData(processedData);
      } catch (err: any) {
        if (err.response && err.response.status === STATUS_CODE.FORBIDDEN) {
          setIsAuthorized(false);
        } else {
          openNotificationWithIcon(
            "",
            err.response?.data?.message || "Error fetching data",
            "error"
          );
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [timeGranularity, data, dataKey]);

  const barChartData = useMemo(() => {
    const hasData = chartData.length > 0;

    return {
      labels: hasData ? chartData.map((item) => item.month) : ["No Data"],
      datasets: [
        {
          label: labelText,
          data: hasData ? chartData.map((item) => item.value) : [0],
          backgroundColor: (context: any) => {
            const ctx = context.chart.ctx;
            const gradient = ctx.createLinearGradient(0, 0, 0, 400);
            gradient.addColorStop(0, "#98DAFF");
            gradient.addColorStop(1, "#407CCA");
            return gradient;
          },
          borderColor: (context: any) => {
            const ctx = context.chart.ctx;
            const gradient = ctx.createLinearGradient(0, 0, 0, 400);
            gradient.addColorStop(0, "rgb(152 218 255 / 50%)");
            gradient.addColorStop(1, "rgb(64 124 202)");
            return gradient;
          },
          borderWidth: 1,
          borderRadius: 5,
        },
      ],
    };
  }, [chartData, labelText]);

  const options = useMemo(() => {
    return {
      scales: {
        x: {
          type: "category",
          stacked: true,
          grid: {
            drawOnChartArea: false,
          },
        },
        y: {
          stacked: true,
          beginAtZero: true,
        },
      },
      maintainAspectRatio: false,
      responsive: true,
      height: 400,
      plugins: {
        legend: {
          position: "bottom" as const,
        },
      },
    } as ChartOptions<"bar">;
  }, []);

  const onTimeGranularityChange = (granularity: string) => {
    setTimeGranularity(granularity);
  };

  if (!isAuthorized) {
    return <NotAccessibleFallback />;
  }

  return (
    <Spin spinning={loading}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div>
          <h4 className="summary-title">{title}</h4>
          <p>{description}</p>
        </div>
        <div style={{ paddingBottom: "20px" }}>
          <Select
            value={timeGranularity}
            style={{ width: 120, height: 40 }}
            onChange={onTimeGranularityChange}
          >
            {availableGranularities.map((gran) => (
              <Option key={gran} value={gran}>
                {gran.charAt(0).toUpperCase() + gran.slice(1)}
              </Option>
            ))}
          </Select>
        </div>
      </div>
      <div style={{ height: "375px" }}>
        <Bar data={barChartData} options={options} />
      </div>
    </Spin>
  );
};
